import {Document, Font, Image, Page, View} from '@react-pdf/renderer'
import {ColorLegend} from './ColorLegend'
import PersonaLegend from './PersonaLegend'
import {styles} from './Styles'
import {HouseholdLegend, isEnrolled} from './HouseholdLegend'
import {getFormatedDataForChip} from './helper'
import PropTypes from 'prop-types'
import {Chip} from './Chip'
import PDFLogo from '../../../../PDFElements/PDFLogo'
import Footer from '../../../../PDFElements/Footer'
import TimeStamp from '../../../../PDFElements/TimeStamp'
import QuickStat from './QuickStat'
import OpenSansRegular from '../../../../../fonts/open-sans/OpenSans-Regular.ttf'
import OpenSansSemiBold from '../../../../../fonts/open-sans/OpenSans-SemiBold.ttf'
import OpenSansBold from '../../../../../fonts/open-sans/OpenSans-Bold.ttf'

const PDFTemplate = ({
  mapRef,
  filters,
  // New filter management props
  appliedFilters,
  filterMetadata,
  filterConfig,
  customizations, // Custom filter names from API
  outreachData,
  // Legacy props (kept for backward compatibility)
  sdohData,
  selectedPOI,
  selectedFocusLayer,
  timeStamp,
  quickStatData,
  poiImageData,
  personas,
  personasConfig,
  personasToggle,
  personaPercentage,
  prismProgram
}) => {
  // Use new filter management system for chip formatting
  const chipValues = getFormatedDataForChip(
    appliedFilters,
    filterMetadata,
    filterConfig,
    customizations, // Pass customizations for custom filter names
    filters, // stateAndHealthFilters from breadcrumbs
    outreachData
  )
  const checkIsEnrolled = isEnrolled(outreachData)

  // Font ttf are loaded from src/fonts folder(download from google fonts github repo: https://github.com/googlefonts/opensans/tree/main/fonts/ttf)
  Font.register({
    family: 'Open Sans',
    fonts: [
      {src: OpenSansRegular}, // font-style: normal, font-weight: normal
      {src: OpenSansSemiBold, fontWeight: 600},
      {src: OpenSansBold, fontWeight: 700}
    ]
  })

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        {TimeStamp(timeStamp)}
        <PDFLogo prismProgram={prismProgram} />
        <View style={{paddingHorizontal: 15}}>
          <View style={styles.chipContainer} key='chip'>
            {chipValues?.map((chipValue, index) => {
              return <View key={`chip-${chipValue.label}-${index}`}>{Chip(chipValue)}</View>
            })}
          </View>
          <View style={styles.quickStatContainer} key='quickstat'>
            {QuickStat(quickStatData)}
          </View>
          <Image style={styles.mapImage} src={mapRef?.getCanvas().toDataURL('image/png')} key='map-image' />
          <View style={styles.legendContainer} key='legends'>
            {!personasToggle && ColorLegend}
            {(!personasToggle || checkIsEnrolled) && HouseholdLegend(selectedFocusLayer, outreachData)}
          </View>
          {personasToggle && (
            <PersonaLegend personas={personas} personasConfig={personasConfig} personaPercentage={personaPercentage} />
          )}
          {selectedPOI?.length > 0 && (
            <View style={styles.poiLegendContainer} key='poi-legend'>
              <Image src={poiImageData} style={styles.poiLegendImage} />
            </View>
          )}
        </View>
        {Footer}
      </Page>
    </Document>
  )
}

PDFTemplate.propTypes = {
  mapRef: PropTypes.object,
  filters: PropTypes.array,
  // New filter management props
  appliedFilters: PropTypes.object,
  filterMetadata: PropTypes.object,
  filterConfig: PropTypes.object,
  customizations: PropTypes.object, // Custom filter names from API
  outreachData: PropTypes.shape({
    isActive: PropTypes.bool,
    type: PropTypes.string,
    enrolledValue: PropTypes.number,
    selectedOptions: PropTypes.array,
    activeFilterOptions: PropTypes.array,
    params: PropTypes.object
  }),
  // Legacy/shared props
  sdohData: PropTypes.object, // Kept for backward compatibility
  selectedPOI: PropTypes.array,
  selectedFocusLayer: PropTypes.string,
  timeStamp: PropTypes.string,
  quickStatData: PropTypes.array,
  poiImageData: PropTypes.string,
  personas: PropTypes.arrayOf(PropTypes.string).isRequired,
  personasConfig: PropTypes.object.isRequired,
  personasToggle: PropTypes.bool.isRequired,
  personaPercentage: PropTypes.arrayOf(
    PropTypes.shape({
      persona: PropTypes.string.isRequired,
      percentage: PropTypes.number.isRequired,
      personaCount: PropTypes.number.isRequired
    })
  ).isRequired,
  prismProgram: PropTypes.string
}

export default PDFTemplate

/**
 * List of functions and their purpose:
 *
 * - PDFTemplate: Generates a PDF document with map visuals, legends, quick stats, and filters for export.
 */
